import React from 'react';
import {useSearchParams} from 'react-router-dom';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {TAGS_FILTER_PARAM} from 'shared/lib/filterHelper';
import {truncate} from 'shared/lib/format/string';
import useClearTagsFilter from 'shared/lib/hooks/useClearTagsFilter';
import useOpenState from 'shared/lib/hooks/useOpenState';
import useToggleTagFilter from 'shared/lib/hooks/useToggleTagFilter';
import IconButton from 'shared/ui/IconButton/IconButton';
import Tags from 'shared/ui/icons/TablesMenu/Tags';

import {tagsAPI} from '../../api/tagsAPI';
import {sortTags} from '../../lib/tagsSort';
import useTagsSort from '../../model/useTagsSort';
import TagsManageModal from '../TagsManageModal/TagsManageModal';

import styles from './TagsBar.module.scss';

interface ITagsBarProps {
    disabled?: boolean,
}

const TagsBar: React.FC<ITagsBarProps> = ({disabled}) => {
    const {data: tags = []} = tagsAPI.useGetTagsQuery();
    const [searchParams] = useSearchParams();
    const toggleTag = useToggleTagFilter();
    const clearTags = useClearTagsFilter();
    const {mode} = useTagsSort();
    const manageModal = useOpenState();

    const activeTags = searchParams.getAll(TAGS_FILTER_PARAM);
    const sortedTags = sortTags(tags, mode);

    return (
        <div className={styles.container}>
            <IconButton
                className={styles.manage}
                icon={<Tags/>}
                title={t`Управление тегами`}
                aria-label={t`Управление тегами`}
                onClick={manageModal.open}
            />

            <div className={styles.content}>
                {sortedTags.length === 0 ? (
                    <button
                        type="button"
                        className={styles.empty}
                        title={t`Теги можно создать здесь или в форме монитора и сервера`}
                        onClick={manageModal.open}
                    >
                        {t`Тегов пока нет — добавьте`}
                    </button>
                ) : sortedTags.map(tag => {
                    const isActive = activeTags.includes(tag.label);

                    return (
                        <span
                            key={tag.id}
                            className={cn(
                                styles.tag,
                                !disabled && styles.clickable,
                                isActive && styles.active,
                            )}
                            onClick={() => !disabled && toggleTag(tag.label)}
                            title={tag.label}
                        >
                            {truncate(tag.label)}
                            {isActive && <span className={styles.remove} aria-hidden="true">×</span>}
                        </span>
                    );
                })}

                {activeTags.length > 0 && (
                    <button
                        type="button"
                        className={styles.reset}
                        title={t`Сбросить фильтр по тегам`}
                        aria-label={t`Сбросить фильтр по тегам`}
                        disabled={disabled}
                        onClick={clearTags}
                    >
                        {t`Сбросить`}
                    </button>
                )}
            </div>

            <TagsManageModal open={manageModal.isOpen} handleClose={manageModal.close}/>
        </div>
    );
};

export default TagsBar;
